import {useLegacyContract} from '@api'
import {formatCurrency, FormFieldItem, getUserData, isSuperAdmin} from '@common'
import {FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef} from '@mui/x-data-grid'
import {pageLinksAtom, getCompanyCurrencyAtom, getUserSelectionAtom} from '@state'

import {useEffect, useMemo} from 'react'

import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

export interface ProductData {
  id: string
}

export function useContractLegacyViewModel() {
  const {t} = useTranslation()
  const {id} = useParams()
  const setTitleLinks = useSetAtom(pageLinksAtom)

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const getCompanyCurrency = useAtomValue(getCompanyCurrencyAtom)
  const currency = userSelection ? getCompanyCurrency(userSelection?.companyId) : 'EUR'

  const {data: legacyContractResponse} = useLegacyContract(id)

  const contractData = useMemo(() => {
    if (!legacyContractResponse) return
    else return {...legacyContractResponse, dealer: legacyContractResponse.supplierName}
  }, [legacyContractResponse])

  const productRows = useMemo(() => {
    if (!legacyContractResponse) return []
    else return legacyContractResponse.lines.map(item => ({...item, name: item.equipmentDescription}))
  }, [legacyContractResponse])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Contract search']), link: NavigationPathKey.CONTRACT_SEARCH},
        {title: t(TranslationKey['Contract overview'])},
      ]),
    [],
  )

  /**
   * Contract table columns configuration
   */
  const productColumns: GridColDef[] = [
    {field: 'name', headerName: t(TranslationKey.Name), flex: 1.2},
    {field: 'serialNumber', headerName: t(TranslationKey['Serial number']), flex: 1.2},
    {
      field: 'originalValue',
      headerName: t(TranslationKey['Value']),
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: value => formatCurrency(value, currency),
    },
    {field: 'lineStartDate', headerName: t(TranslationKey['Serial number']), flex: 1.2},
    {field: 'contractLength', headerName: t(TranslationKey['Contract length']), flex: 1.2},
    {field: 'currentPeriod', headerName: t(TranslationKey.Period), flex: 1.2},
    ...(isSuperAdmin()
      ? [
          {
            field: 'presentValue',
            headerName: t(TranslationKey['Present value']),
            flex: 1,
            align: 'right',
            headerAlign: 'right',
            valueFormatter: value => formatCurrency(value, currency),
          } as GridColDef,
        ]
      : []),
    {
      field: 'residualValue',
      headerName: t(TranslationKey['Residual value']),
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: value => formatCurrency(value, currency),
    },
  ]

  const contractStatus = useMemo(() => contractData?.contractStatus, [contractData])

  /**
   * Contract form field configuration
   */
  const contractOverviewFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Contract code']),
      text: contractData?.contractNumber || '-',
    },
    // {
    //   fieldType: FormFieldType.LABEL,
    //   label: t(TranslationKey['Contract Type']),
    //   text: contractData?.contractTypeNumber || '-',
    // },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Dealer),
      text: contractData?.supplierName,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Customer),
      text: contractData?.customerName || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Customer email']),
      text: contractData?.customerEmail || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Customer phone']),
      text: contractData?.customerPhoneNumber || '-',
    },
  ]

  const contractPricingFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Original value']),
      text: formatCurrency(contractData?.originalValue || 0, currency),
    },
    ...(isSuperAdmin()
      ? [
          {
            fieldType: FormFieldType.LABEL,
            label: t(TranslationKey['Present value']),
            text: formatCurrency(contractData?.presentValue || 0, currency),
          },
        ]
      : []),
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Residual value']),
      text: formatCurrency(contractData?.residualValue || 0, currency),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Monthly fee']),
      text: formatCurrency(contractData?.monthlyPaymentInkVAT || 0, currency),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Monthly fee excluding VAT']),
      text: formatCurrency(contractData?.monthlyPaymentExVAT || 0, currency),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Unpaid invoices']),
      text: String(contractData?.numberOfUnpaidInvoices || 0),
    },
  ]

  return {
    contractOverviewFormFields,
    contractPricingFormFields,
    productColumns,
    productRows,
    contractStatus,
    t,
  }
}
